/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 컬러 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/icon/Icon.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 27.
 */
import * as parent from '@components/form/field/Field';
import { Segmented } from '@components/button/Segmented';
import { Select } from '../select/Select';
import { Local } from '@models/store/list/Local';
import { Image } from '../image/Image';
import { Text } from '../text/Text';
import { Language } from '@utils/Language';
import './Icon.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Icon extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'icon';

    /**
     * 아이콘 타입선택 버튼
     */
    iconType: Segmented = null;

    /**
     * 아이콘 선택필드
     */
    iconSelect: Select = null;

    /**
     * 아이콘 설정 입력필드 (스타일시트이거나 URL 입력시 사용)
     */
    iconText: Text = null;

    /**
     * 아이콘 파일필드
     */
    iconFile: Image = null;

    /**
     * 숫자 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Icon> = null) {
        super(properties);
    }

    /**
     * 아이콘 타입선택 버튼을 정의한다.
     *
     * @returns 아이콘 타입선택 버튼
     */
    getIconType(): Segmented {
        if (this.iconType === null) {
            this.iconType = new Segmented({
                value: 'NONE',
                buttons: [
                    { text: Language.get('components.form.field.icon.none'), value: 'NONE' },
                    { text: Language.get('components.form.field.icon.icon'), value: 'ICON' },
                    { text: Language.get('components.form.field.icon.class'), value: 'CLASS' },
                    { text: Language.get('components.form.field.icon.url'), value: 'URL' },
                    { text: Language.get('components.form.field.icon.file'), value: 'FILE' },
                ],
                listeners: {
                    change: async (_, value) => {
                        if (value == 'ICON') {
                            this.getIconSelect().show();
                        } else {
                            this.getIconSelect().hide();
                        }

                        if (value == 'CLASS' || value == 'URL') {
                            this.getIconText().show();
                        } else {
                            this.getIconText().hide();
                        }

                        if (value == 'FILE') {
                            this.getIconFile().show();
                        } else {
                            this.getIconFile().hide();
                        }
                    },
                },
            });
        }

        return this.iconType;
    }

    /**
     * 아이콘 선택필드를 가져온다.
     *
     * @returns 아이콘 선택필드
     */
    getIconSelect(): Select {
        if (this.iconSelect === null) {
            // @todo Select 에 목록형 말고, 카드형 추가필요
            this.iconSelect = new Select({
                hidden: true,
                store: new Local({
                    provider: {
                        dataFields: ['value'],
                        datas: [['mi mi-plus'], ['mi mi-trash']], // @todo 아이콘 추가필요
                    },
                }),
                displayField: 'value',
                valueField: 'value',
                value: 'mi mi-plus',
                renderer: (display) => {
                    if (display) {
                        return `<i class="${display}"></i>`;
                    }
                    return '';
                },
                listRenderer: (display) => {
                    return `<i class="${display}"></i>`;
                },
            });
        }

        return this.iconSelect;
    }

    /**
     * 아이콘 설정 입력필드를 가져온다.
     *
     * @returns 아이콘 설정 입력필드
     */
    getIconText(): Text {
        if (this.iconText === null) {
            this.iconText = new Text({
                hidden: true,
                listeners: {
                    change: async (field, value) => {},
                },
            });
        }

        return this.iconText;
    }

    /**
     * 아이콘 파일필드를 가져온다.
     *
     * @returns 아이콘 파일필드
     */
    getIconFile(): Image {
        if (this.iconFile === null) {
            this.iconFile = new Image({
                hidden: true,
                imageName: 'icon',
            });
        }

        return this.iconFile;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<number> {
        // @todo 필드가 완성되기 전까지 언제나 NULL 을 반환한다.
        return null;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     */
    async setValue(value: string | null, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        // @todo 필드가 완성되기 전까지 아무것도 하지 않는다.

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        let validation = this.getValidationHistory(value);
        if (validation === null) {
            validation = { valid: true };

            const isBlank = await this.isBlank(value);
            if (this.allowBlank === false && isBlank == true) {
                validation.valid = false;
                validation.message = await Language.getError('REQUIRED').getText();
            } else if (typeof this.validator == 'function') {
                const results = await this.validator(value, this);
                if (typeof results == 'boolean') {
                    validation.valid = results;
                } else {
                    validation.valid = false;
                    validation.message = results;
                }
            } else {
                // @todo 아이콘 종류에 따른 형식체크
            }
        }

        return validation;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getIconType().$getComponent());
        await this.getIconType().render();

        this.$getContent().append(this.getIconSelect().$getComponent());
        await this.getIconSelect().render();

        this.$getContent().append(this.getIconText().$getComponent());
        await this.getIconText().render();

        this.$getContent().append(this.getIconFile().$getComponent());
        await this.getIconFile().render();
    }
}
